import React from 'react';
import {
  walletTransactionsService,
  WalletTransactionRow,
} from '@/services/walletTransactionsService';
import { useWallet } from '@/contexts/WalletContext';
import styles from '@/styles/components/profile/ProfileTransactions.module.css';

export default function ProfileTransactions() {
  const { state } = useWallet();
  const [rows, setRows] = React.useState<WalletTransactionRow[]>([]);
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);
  const [loading, setLoading] = React.useState(false);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const pagedRows = React.useMemo(() => {
    const start = (page - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, page, pageSize]);

  React.useEffect(() => {
    let active = true;
    const run = async () => {
      if (!state.address) return;
      setLoading(true);
      try {
        const data = await walletTransactionsService.getWalletTransactions(
          state.address
        );
        if (!active) return;
        setRows(data);
      } finally {
        if (active) setLoading(false);
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [state.address]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>Account details</div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}>Balance tokens</th>
              <th className={styles.th}>Balance $</th>
              <th className={styles.th}>Type of operation</th>
              <th className={styles.th}>Quantity</th>
              <th className={styles.th}>Source</th>
              <th className={styles.th}>Destination</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className={styles.td} colSpan={6} style={{ textAlign: 'center' }}>Loading...</td>
              </tr>
            ) : pagedRows.length === 0 ? (
              <tr>
                <td className={styles.td} colSpan={6} style={{ textAlign: 'center' }}>No transactions</td>
              </tr>
            ) : (
              pagedRows.map((r, i) => (
                <tr key={`${r.txHash}-${i}`}>
                  <td className={styles.td}>{r.token} {r.amount}</td>
                  <td className={styles.td}>$--</td>
                  <td className={styles.td}>{r.operation}</td>
                  <td className={styles.td}>{r.quantity}</td>
                  <td className={styles.td}>{r.source}</td>
                  <td className={styles.td}>{r.destination}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className={styles.footer}>
        <div className={styles.row}>
          <button className={styles.btn} disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))}>{'<'}</button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, idx) => idx + 1).map(n => (
            <button key={n} className={styles.btn} style={{ background: page === n ? 'var(--surface-surface_3, #eef2ff)' : 'var(--surface-surface_0, #fff)' }} onClick={() => setPage(n)}>{n}</button>
          ))}
          <button className={styles.btn} disabled={page === totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>{'>'}</button>
          <span style={{ marginLeft: 8 }}>Go to page:</span>
          <input className={styles.pill} style={{ width: 80 }} placeholder="Number" onKeyDown={e => {
            if (e.key === 'Enter') {
              const v = parseInt((e.target as HTMLInputElement).value, 10);
              if (!isNaN(v)) setPage(Math.min(Math.max(1, v), totalPages));
            }
          }} />
          <button className={styles.btn}>Go</button>
        </div>
        <div className={styles.row}>
          <span>Results per page:</span>
          <select className={styles.pill} value={pageSize} onChange={e => { setPageSize(parseInt(e.target.value, 10)); setPage(1); }}>
            {[10, 20, 50].map(size => <option key={size} value={size}>{size}</option>)}
          </select>
        </div>
      </div>
    </div>
  );
}
